import React, { useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { toast } from 'react-toastify';
import MainLayout from '../../components/Layout/MainLayout';
import reportsService from '../../services/reportsService';
import { exportToCSV, exportToPDF } from '../../utils/reports/exportUtils';

const PaymentReportPage = () => {
  const now = new Date();
  const [filters, setFilters] = useState({ month: now.getMonth() + 1, year: now.getFullYear(), hrName: '', status: '', page: 1, limit: 10 });
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState({ data: [], pagination: {} });

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await reportsService.getPaymentReport(filters);
      if (response.success) {
        setPayload(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load payment report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters.month, filters.year, filters.hrName, filters.status, filters.page, filters.limit]);

  const totalPayout = useMemo(
    () => payload.data.reduce((sum, row) => sum + Number(row.totalSalary || 0), 0),
    [payload.data]
  );

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto p-4 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <h1 className="text-3xl font-bold text-charcoal">Payment Report</h1>
          <div className="flex gap-2">
            <button
              onClick={() => exportToCSV(
                `payment-report-${filters.month}-${filters.year}`,
                [
                  { label: 'HR Name', key: 'hrName' },
                  { label: 'Base Salary', key: 'baseSalary' },
                  { label: 'Incentive', key: 'incentive' },
                  { label: 'Total Salary', key: 'totalSalary' },
                  { label: 'Status', key: 'status' }
                ],
                payload.data
              )}
              className="px-4 py-2 bg-olive text-white rounded-lg text-sm"
            >Export CSV</button>
            <button onClick={exportToPDF} className="px-4 py-2 bg-charcoal text-white rounded-lg text-sm">Export PDF</button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Stat label="Total HR Payments" value={payload.pagination?.total || 0} />
          <Stat label="Visible Payout" value={`₹${totalPayout.toFixed(2)}`} />
          <Stat label="Period" value={`${filters.month}/${filters.year}`} />
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4 grid grid-cols-1 md:grid-cols-6 gap-3">
          <input type="number" min="1" max="12" value={filters.month} onChange={(e) => setFilters((p) => ({ ...p, month: Number(e.target.value), page: 1 }))} className="border rounded-lg px-3 py-2" />
          <input type="number" min="2000" value={filters.year} onChange={(e) => setFilters((p) => ({ ...p, year: Number(e.target.value), page: 1 }))} className="border rounded-lg px-3 py-2" />
          <input type="text" placeholder="HR Name" value={filters.hrName} onChange={(e) => setFilters((p) => ({ ...p, hrName: e.target.value, page: 1 }))} className="border rounded-lg px-3 py-2" />
          <select value={filters.status} onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value, page: 1 }))} className="border rounded-lg px-3 py-2">
            <option value="">All status</option>
            <option value="Paid">Paid</option>
            <option value="Pending">Pending</option>
          </select>
          <select value={filters.limit} onChange={(e) => setFilters((p) => ({ ...p, limit: Number(e.target.value), page: 1 }))} className="border rounded-lg px-3 py-2">
            <option value={10}>10 / page</option><option value={20}>20 / page</option><option value={50}>50 / page</option>
          </select>
          <button onClick={() => setFilters((p) => ({ ...p, hrName: '', status: '', page: 1 }))} className="px-4 py-2 bg-gray-100 rounded-lg">Clear</button>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-5">
          <h2 className="text-lg font-semibold text-charcoal mb-4">Total Salary by HR</h2>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={payload.data.map((x) => ({ name: x.hrName, totalSalary: x.totalSalary }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="totalSalary" fill="#10B981" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50"><tr><Th>HR</Th><Th>Month</Th><Th>Base Salary</Th><Th>Gross Salary</Th><Th>Payable Gross</Th><Th>Absent Days</Th><Th>Total Deductions</Th><Th>LOP</Th><Th>Incentive</Th><Th>Total Salary</Th><Th>Status</Th><Th>Transaction ID</Th><Th>UAN No</Th><Th>Txn Doc</Th><Th>Paid At</Th><Th>Payslip</Th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={16} className="px-4 py-6 text-center">Loading...</td></tr> :
                payload.data.length === 0 ? <tr><td colSpan={16} className="px-4 py-6 text-center">No records</td></tr> :
                payload.data.map((row) => (
                  <tr key={row._id} className="border-t">
                    <Td>{row.hrName}</Td>
                    <Td>{`${row.month}/${row.year}`}</Td>
                    <Td>₹{Number(row.baseSalary || 0).toFixed(2)}</Td>
                    <Td>₹{Number(row.grossSalary || 0).toFixed(2)}</Td>
                    <Td>₹{Number(row.payableGross || 0).toFixed(2)}</Td>
                    <Td>{Number(row.absentDays || 0).toFixed(2)}</Td>
                    <Td>₹{Number(row.totalDeductions || 0).toFixed(2)}</Td>
                    <Td>₹{Number(row.lopAmount || 0).toFixed(2)}</Td>
                    <Td>₹{Number(row.incentive || 0).toFixed(2)}</Td>
                    <Td>₹{Number(row.totalSalary || 0).toFixed(2)}</Td>
                    <Td>{row.status}</Td>
                    <Td>{row.transactionId || '-'}</Td>
                    <Td>{row.uanNo || '-'}</Td>
                    <Td>
                      {row.transactionDocumentUrl ? (
                        <a href={row.transactionDocumentUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">View Doc</a>
                      ) : '-'}
                    </Td>
                    <Td>{row.paidAt ? new Date(row.paidAt).toLocaleString() : '-'}</Td>
                    <Td>
                      {row.payslipUrl ? (
                        <a href={row.payslipUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">View Slip</a>
                      ) : '-'}
                    </Td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <Pagination pagination={payload.pagination} onPageChange={(nextPage) => setFilters((p) => ({ ...p, page: nextPage }))} />
      </div>
    </MainLayout>
  );
};

const Stat = ({ label, value }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
    <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
    <p className="text-2xl mt-2 font-semibold text-charcoal">{value}</p>
  </div>
);
const Th = ({ children }) => <th className="px-4 py-3 text-left text-xs uppercase text-gray-600">{children}</th>;
const Td = ({ children }) => <td className="px-4 py-3">{children}</td>;
const Pagination = ({ pagination, onPageChange }) => (
  <div className="flex items-center justify-between">
    <p className="text-sm text-gray-600">Page {pagination?.page || 1} of {pagination?.totalPages || 1}</p>
    <div className="flex gap-2">
      <button className="px-3 py-1 border rounded disabled:opacity-50" disabled={!pagination?.page || pagination.page <= 1} onClick={() => onPageChange(pagination.page - 1)}>Previous</button>
      <button className="px-3 py-1 border rounded disabled:opacity-50" disabled={!pagination?.page || pagination.page >= pagination.totalPages} onClick={() => onPageChange(pagination.page + 1)}>Next</button>
    </div>
  </div>
);

export default PaymentReportPage;
